import React, {useState, useEffect} from 'react'
import {useParams, useNavigate} from 'react-router-dom'

import MatrixCard from '../../../Components/MatrixCard'

import ReactPaginate from 'react-paginate'
import './EOFYSaleSubSections/ReportCss.scss'
import {
  useGetFlowReportQuery,
  useGetFlowReportTemplateQuery,
} from '../../../../services/cellcast.v2'
import {Row, Col} from 'react-bootstrap'
import {useI18n} from '../../../Utils/_helpers/useI18n'
import {FlowTemplateStats} from '../Flow/WorkFlow/FlowTypes'

const FlowReport: React.FC = () => {
  const {t} = useI18n()
  const navigate = useNavigate()
  const {id} = useParams()
  // Date range (default: last 7 days)
  const today = new Date()
  const sevenDaysAgo = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000)
  const [dateRange, setDateRange] = useState<[Date | null, Date | null]>([sevenDaysAgo, new Date()])
  const [startDate, endDate] = dateRange

  const toISOStartOfDay = (d: Date) =>
    new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0, 0).toISOString()
  const toISOEndOfDay = (d: Date) =>
    new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59, 999).toISOString()

  const dateRangeParam =
    startDate && endDate ? `${toISOStartOfDay(startDate)},${toISOEndOfDay(endDate)}` : undefined

  const {
    data: flowReport,
    isLoading: isLoadingReport,
    error: errorReport,
    refetch: fetchFlowReport,
  } = useGetFlowReportQuery({id: id!, dateRange: dateRangeParam})

  const {
    data: templateStatsResp,
    isLoading: isLoadingTemplates,
    error: errorTemplates,
  } = useGetFlowReportTemplateQuery({id: id!, dateRange: dateRangeParam})
  const templateStats: FlowTemplateStats[] =
    templateStatsResp && Array.isArray(templateStatsResp.data?.templateMessages)
      ? templateStatsResp.data.templateMessages
      : []

  useEffect(() => {
    fetchFlowReport()
  }, [id, dateRangeParam])
  const [pageSize, setPageSize] = useState({page: 1, size: 10})

  useEffect(() => {
    setPageSize({page: 1, size: 10})
  }, [id])

  if (isLoadingReport || isLoadingTemplates) {
    return <div className='flowreport-loading'>{t('FLOWREPORT.LOADING')}</div>
  }
  if (errorReport || errorTemplates) {
    return <div className='flowreport-loading text-danger'>{t('FLOWREPORT.ERROR')}</div>
  }
  if (!flowReport?.data) {
    return <div className='flowreport-loading text-warning'>{t('FLOWREPORT.NO_DATA_YET')}</div>
  }

  const report = flowReport.data

  const formatDisplay = (d: Date | null) => (d ? d.toLocaleDateString() : '-')

  const navigateBack = () => {
    if (window.history.length > 1) {
      navigate(-1)
    } else {
      navigate('/automation')
    }
  }

  const BuildMatrixBulkMessage = () => {
    const rates = [
      report.bounceRate || {value: 0, type: 'bounce_rate', growthRate: 0},
      report.ctrRate || {value: 0, type: 'ctr_rate', growthRate: 0},
      report.replyRate || {value: 0, type: 'reply_rate', growthRate: 0},
      report.unsubscribeRate || {value: 0, type: 'unsubscribe_rate', growthRate: 0},
    ]
    return (
      <div className='card p-8 flowreport-matrix-section'>
        <h3 className='flowreport-performance-title'>{t('FLOWREPORT.PERFORMANCE')}</h3>
        <h5 className='flowreport-performance-subtitle'>
          {t('FLOWREPORT.DATE_RANGE', {
            from: formatDisplay(startDate),
            to: formatDisplay(endDate),
          })}
        </h5>
        <Row className='flowreport-matrix-row'>
          {rates.map((rate, idx) => (
            <Col key={rate.type || idx} xs={12} sm={6} md={3} className='flowreport-matrix-col'>
              <MatrixCard
                value={typeof rate.value === 'number' ? rate.value : 0}
                type={rate.type}
                growthRate={typeof rate.growthRate === 'number' ? rate.growthRate : 0}
                tooltipText={rate.type ? t(`FLOWREPORT.${rate.type.toUpperCase()}`) : ''}
              />
            </Col>
          ))}
        </Row>
      </div>
    )
  }

  const TitleComponent = () => {
    return (
      <div className='card p-8 flowreport-title-section shadow-sm border-0'>
        <div className='d-flex flex-column flex-md-row justify-content-between align-items-md-center w-100'>
          <div className='mb-3 mb-md-0'>
            <div className='d-flex align-items-center gap-3 mb-2'>
              <span
                className='me-2'
                style={{cursor: 'pointer'}}
                onClick={() => navigateBack()}
                title={t('COMMON.ACTIONS.BACK')}
              >
                <i className='bi bi-arrow-left fs-4 text-primary'></i>
              </span>
              <span
                className={`badge flowreport-badge ${
                  report.status === 'active'
                    ? 'flowreport-badge-active'
                    : 'flowreport-badge-inactive'
                }`}
              >
                {report.status === 'active' ? t('FLOWREPORT.ACTIVE') : t('FLOWREPORT.INACTIVE')}
              </span>

              <span className='text-muted fw-bold'>
                {t('FLOWREPORT.STARTED', {date: formatDisplay(startDate)})}
              </span>
            </div>
            <h3 className='fw-bold mb-0'>
              {t('FLOWREPORT.TITLE.FLOW_NAME', {flowName: report.flowName || ''})}
            </h3>
          </div>
          <div className='row g-3 text-start'>
            <div className='col-6'>
              <div className='text-muted fw-bold'>
                {t('FLOWREPORT.DAYS_ACTIVE')}:{' '}
                <span className='fw-bold text-dark'>{report?.dayActive || 0}</span>
              </div>
            </div>
            <div className='col-6'>
              <div className='text-muted fw-bold'>
                {t('FLOWREPORT.TOTAL_IN_PROGRESS')}:{' '}
                <span className='fw-bold text-dark'>{report?.totalInProgress || 0}</span>
              </div>
            </div>
            <div className='col-6'>
              <div className='text-muted fw-bold'>
                {t('FLOWREPORT.TOTAL_STARTED')}:{' '}
                <span className='fw-bold text-dark'>{report?.totalStarted || 0}</span>
              </div>
            </div>
            <div className='col-6'>
              <div className='text-muted fw-bold'>
                {t('FLOWREPORT.TOTAL_COMPLETED')}:{' '}
                <span className='fw-bold text-dark'>{report?.totalCompleted || 0}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  const TableTemplate = () => {
    return (
      <>
        <div className='card p-8 flowreport-step-analysis'>
          <div className='d-flex justify-content-between align-items-center mb-3'>
            <div className='flex flex-column'>
              <h4 className='fw-bold mb-1'>{t('FLOWREPORT.STEP_ANALYSIS')}</h4>
              <h5 className='fw-bold mb-1 text-muted'>
                {t('FLOWREPORT.DATE_RANGE', {
                  from: formatDisplay(startDate),
                  to: formatDisplay(endDate),
                })}
                - {templateStats?.length || 0} {t('FLOWREPORT.SENDS').toLowerCase()}
              </h5>
            </div>
            <div className='d-flex align-items-center gap-3'>
              <input
                type='date'
                className='form-control form-control-sm'
                value={
                  startDate
                    ? new Date(startDate.getTime() - startDate.getTimezoneOffset() * 60000)
                        .toISOString()
                        .slice(0, 10)
                    : ''
                }
                onChange={(e) => {
                  const v = e.target.value ? new Date(e.target.value) : null
                  setDateRange([v, endDate])
                }}
              />
              <span className='text-muted'>to</span>
              <input
                type='date'
                className='form-control form-control-sm'
                value={
                  endDate
                    ? new Date(endDate.getTime() - endDate.getTimezoneOffset() * 60000)
                        .toISOString()
                        .slice(0, 10)
                    : ''
                }
                onChange={(e) => {
                  const v = e.target.value ? new Date(e.target.value) : null
                  setDateRange([startDate, v])
                }}
              />
            </div>
          </div>
          <div className='flowreport-card-body card-body py-3 px-14 bg-white'>
            <div className='flowreport-table-responsive table-responsive'>
              <table className='flowreport-table table table-row-dashed table-row-gray-200 align-middle gs-0 gy-4'>
                <thead>
                  <tr className='fw-400 text-muted'>
                    <th className='ps-4 w-40px'></th>
                    <th className='ps-4 min-w-100px'>{t('FLOWREPORT.GENERAL')}</th>
                    <th className='min-w-250px'>{t('FLOWREPORT.MESSAGE_NAME')}</th>
                    <th className='min-w-100px' title={t('FLOWREPORT.TOOLTIP.SENDS')}>
                      {t('FLOWREPORT.SENDS')}
                    </th>
                    <th className='min-w-100px' title={t('FLOWREPORT.TOOLTIP.REPLIES')}>
                      {t('FLOWREPORT.REPLIES')}
                    </th>
                    <th className='min-w-100px' title={t('FLOWREPORT.TOOLTIP.UNSUBSCRIBED')}>
                      {t('FLOWREPORT.UNSUBSCRIBED')}
                    </th>
                    <th className='min-w-100px' title={t('FLOWREPORT.TOOLTIP.CLICK_RATE')}>
                      {t('FLOWREPORT.CLICK_RATE')}
                    </th>
                    <th className='min-w-100px' title={t('FLOWREPORT.TOOLTIP.BOUNCED')}>
                      {t('FLOWREPORT.BOUNCED')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {(templateStats || [])
                    .slice((pageSize.page - 1) * pageSize.size, pageSize.page * pageSize.size)
                    .map((step, idx) => (
                      <tr key={step.nodeId} className='flowreport-table-row'>
                        <td className='ps-4 w-40px text-center align-middle'>
                          <i className='bi bi-chat-left-text flowreport-icon-sms'></i>
                        </td>
                        <td className='ps-4'>
                          <div className='d-flex align-items-center gap-2'>
                            <span className='flowreport-dot'></span>
                            <span className='fw-bold text-dark'>SMS</span>
                          </div>
                        </td>
                        <td>
                          <span className='text-dark fw-bolder d-block mb-1 fs-6'>
                            {step.templateName || '-'}
                          </span>
                        </td>
                        <td>{typeof step.sent === 'number' ? step.sent : 0}</td>
                        <td>{typeof step.received === 'number' ? step.received : 0}</td>
                        <td>{typeof step.unsubscribed === 'number' ? step.unsubscribed : 0}</td>
                        <td>
                          {typeof step.clickRate?.value === 'number'
                            ? `${step.clickRate.value.toFixed(1)}%`
                            : '—'}
                        </td>
                        <td>{typeof step.bounced === 'number' ? step.bounced : 0}</td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
            <div className='abcd flowreport-pagination'>
              <select
                value={pageSize.size}
                className='SelectSize flowreport-select-size'
                onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                  setPageSize({
                    ...pageSize,
                    size: Number(e.target.value),
                    page: 1,
                  })
                }
              >
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
                <option value={templateStats?.length || 0}>All</option>
              </select>
              <ReactPaginate
                previousLabel='<'
                nextLabel='>'
                pageCount={Math.ceil((templateStats?.length || 0) / pageSize.size)}
                onPageChange={(e) => setPageSize({...pageSize, page: e.selected + 1})}
                containerClassName={'pagiantion'}
                previousLinkClassName={'pagination__link'}
                nextLinkClassName={'pagination__link'}
                disabledClassName={'pagination__link--disabled'}
                activeClassName={'pagination__link--active'}
                forcePage={pageSize.page - 1}
              />
            </div>
          </div>
        </div>
      </>
    )
  }

  return (
    <div className='row'>
      {TitleComponent()}
      {BuildMatrixBulkMessage()}
      {TableTemplate()}
    </div>
  )
}

export default FlowReport
